import { noteScribeAccessError } from "@/helpers/scribeAccess";

type ValidationErrorItem = {
	message?: unknown;
};

/**
 * Error carrying the HTTP status — and the response's machine error code when the
 * body provides one — so callers can branch on those instead of sniffing message copy.
 */
export class ApiError extends Error {
	status: number;
	code?: string;

	constructor(message: string, status: number, code?: string) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		if (code) this.code = code;
	}
}

function formatValidationMessages(value: unknown): string | null {
	if (!Array.isArray(value)) return null;

	const messages = value
		.map((item: ValidationErrorItem | string) => {
			if (typeof item === "string") return item.trim();
			return typeof item?.message === "string" ? item.message.trim() : "";
		})
		.filter(Boolean);

	return messages.length > 0 ? messages.join("\n") : null;
}

const buildResponseError = async (response: Response | null | undefined): Promise<Error | null> => {
	if (response != null) {
		try {
			const text = await response.text();
			try {
				const json = JSON.parse(text);
				// scribe-api error bodies carry the machine code in `error` (e.g.
				// {error: "BREAKING_SCHEMA_CHANGE", message: "Cannot modify schema: ..."});
				// keep it alongside the human message so callers can branch on it.
				const machineCode = typeof json?.code === "string" ? json.code : typeof json?.error === "string" ? json.error : undefined;
				const validationMessage = formatValidationMessages(json);
				if (validationMessage) return new ApiError(validationMessage, response.status, machineCode);
				if (typeof json?.message === "string") return new ApiError(json.message, response.status, machineCode);
				if (typeof json?.error === "string") return new ApiError(json.error, response.status, machineCode);
				return new ApiError(text, response.status);
			} catch {
				return new ApiError(text, response.status);
			}
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error);
			return new ApiError(message, response.status);
		}
	}
	return new Error("Unknown error");
};

// LSP-17976: every failed Scribe response passes through here, so an archived-membership 403 is
// recorded once for the Spark surfaces (useScribeAccess) instead of being swallowed per call site.
export const responseToError = async (response: Response | null | undefined): Promise<Error | null> => {
	const error = await buildResponseError(response);
	noteScribeAccessError(error);
	return error;
};
